import * as React from 'react'

import { observer } from 'mobx-react'

import { NO_FILL } from '~/client/src/shared/components/MapMarkups/MarkupDrawController'
import Localization from '~/client/src/shared/localization/LocalizationManager'

import type PhotoMarkupStore from './PhotoMarkup.store'
import { layoutText } from './photoMarkupGeometry'
import { TEXT_EDITOR_ATTRIBUTE } from './photoMarkupKeyboard'
import { TEXT_FONT_FAMILY, TEXT_LINE_HEIGHT } from './photoMarkupModel'

interface IProps {
  store: PhotoMarkupStore
}

function PhotoMarkupTextEditor({ store }: IProps): JSX.Element {
  const textareaRef = React.useRef<HTMLTextAreaElement>(null)
  const markup = store.editingText
  const view = store.view

  React.useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.focus()
    textarea.setSelectionRange(textarea.value.length, textarea.value.length)
  }, [markup?.id])

  React.useEffect(() => {
    const frame = requestAnimationFrame(() => textareaRef.current?.focus())
    return () => cancelAnimationFrame(frame)
  }, [
    markup?.color,
    markup?.fillColor,
    markup?.fontSize,
    markup?.bold,
    markup?.italic,
    markup?.underline,
  ])

  if (!markup || !view) return null

  const layout = layoutText(markup, store.unit, store.measure)
  const screen = store.toScreen(markup.at)
  const isAutoWidth = markup.boxWidth == null

  return (
    <textarea
      ref={textareaRef}
      {...{ [TEXT_EDITOR_ATTRIBUTE]: '' }}
      aria-label={Localization.translator.gestureTextGroup}
      value={markup.text}
      wrap={isAutoWidth ? 'off' : 'soft'}
      onChange={event => store.setEditingText(event.target.value)}
      className="tw:absolute tw:z-10 tw:m-0 tw:box-border tw:resize-none tw:overflow-hidden tw:rounded-none tw:border-0 tw:outline-2 tw:outline-primary"
      style={{
        left: screen.x,
        top: screen.y,
        width: layout.width * view.scale,
        height: layout.height * view.scale,
        padding: layout.paddingPx * view.scale,
        fontFamily: TEXT_FONT_FAMILY,
        fontSize: layout.fontPx * view.scale,
        lineHeight: TEXT_LINE_HEIGHT,
        fontWeight: markup.bold ? 700 : 400,
        fontStyle: markup.italic ? 'italic' : 'normal',
        textDecoration: markup.underline ? 'underline' : 'none',
        color: markup.color,
        backgroundColor:
          markup.fillColor === NO_FILL ? 'transparent' : markup.fillColor,
        whiteSpace: isAutoWidth ? 'pre' : 'pre-wrap',
        overflowWrap: 'break-word',
      }}
    />
  )
}

export default observer(PhotoMarkupTextEditor)
